import React, { useState, useMemo } from 'react';
import { X, Search, UserPlus, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import { queueWalkin } from '@/lib/api';

interface Patient {
  id: string;
  name: string;
  age: number;
  gender: string;
  phone: string;
  mrn?: string;
}

interface WalkinModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  doctorId: string;
  clinicId: string;
  tenantId: string;
  patients: Patient[];
  onRegisterNew: (prefilledPhone?: string) => void;
}

export default function WalkinModal({
  isOpen,
  onClose,
  onSuccess,
  doctorId,
  clinicId,
  patients,
  onRegisterNew,
}: WalkinModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [isPriority, setIsPriority] = useState(false);

  const filteredPatients = useMemo(() => {
    if (searchQuery.length < 2) return [];
    const lower = searchQuery.toLowerCase();
    return patients.filter(p =>
      p.name.toLowerCase().includes(lower) ||
      p.phone?.includes(searchQuery)
    ).slice(0, 6);
  }, [searchQuery, patients]);

  const resetAndClose = () => {
    setSearchQuery('');
    setSelectedPatient(null);
    setIsPriority(false);
    setError('');
    onClose();
  };

  const handleAddToQueue = async () => {
    if (!selectedPatient) return;
    setLoading(true);
    setError('');
    try {
      await queueWalkin(selectedPatient.id, doctorId, clinicId, isPriority);
      toast({ title: 'Added to queue', description: `${selectedPatient.name} added as walk-in` });
      onSuccess();
      resetAndClose();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to add to queue';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={resetAndClose} />
      <div className="relative bg-background border border-border rounded-xl shadow-xl w-full max-w-md mx-4 medical-card">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold">Walk-in Patient</h2>
          </div>
          <button onClick={resetAndClose} className="p-1 rounded-lg hover:bg-muted">
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search by name or phone..."
              value={searchQuery}
              onChange={e => { setSearchQuery(e.target.value); setSelectedPatient(null); }}
              className="w-full pl-9 pr-4 py-2.5 border border-input rounded-lg bg-background text-sm focus:outline-none focus:border-ring"
              autoFocus
            />
          </div>

          {/* Results */}
          {filteredPatients.length > 0 && !selectedPatient && (
            <div className="border border-border rounded-lg max-h-48 overflow-y-auto">
              {filteredPatients.map(p => (
                <button
                  key={p.id}
                  onClick={() => { setSelectedPatient(p); setSearchQuery(p.name); }}
                  className="w-full text-left p-3 hover:bg-accent border-b border-border last:border-0 transition-colors"
                >
                  <p className="font-medium text-sm">{p.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {p.age}y &middot; {p.gender} &middot; {p.phone}
                    {p.mrn && <> &middot; MRN: {p.mrn}</>}
                  </p>
                </button>
              ))}
            </div>
          )}

          {/* Selected patient */}
          {selectedPatient && (
            <div className="flex items-center gap-3 p-3 bg-primary/5 border border-primary/20 rounded-lg">
              <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-sm font-bold text-primary">
                  {selectedPatient.name.charAt(0)}
                </span>
              </div>
              <div className="flex-1">
                <p className="font-medium text-sm">{selectedPatient.name}</p>
                <p className="text-xs text-muted-foreground">
                  {selectedPatient.age}y &middot; {selectedPatient.gender} &middot; {selectedPatient.phone}
                </p>
              </div>
              <button
                onClick={() => { setSelectedPatient(null); setSearchQuery(''); }}
                className="p-1 rounded hover:bg-muted"
              >
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>
          )}

          {/* No patient found — Register New */}
          {searchQuery.length >= 2 && filteredPatients.length === 0 && !selectedPatient && (
            <div className="text-center py-4">
              <p className="text-sm text-muted-foreground mb-3">
                No patient found for &quot;{searchQuery}&quot;
              </p>
              <Button
                variant="outline"
                onClick={() => onRegisterNew(searchQuery)}
                className="gap-2"
              >
                <UserPlus className="w-4 h-4" />
                Register New Patient
              </Button>
            </div>
          )}

          {/* Priority */}
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={isPriority}
              onChange={e => setIsPriority(e.target.checked)}
              className="rounded border-input"
            />
            Mark as priority
          </label>

          {/* Error */}
          {error && (
            <p className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-lg">{error}</p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 border-t border-border">
          <Button variant="outline" onClick={resetAndClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleAddToQueue}
            disabled={!selectedPatient || loading}
            className="medical-button-primary"
          >
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Add to Queue
          </Button>
        </div>
      </div>
    </div>
  );
}
